import {
  Constructor,
  type ILabel,
  type IRenderContext,
  LabelStyleBase,
  Rect,
  Size,
  type Visual,
} from '@ardoq/yfiles';
import {
  type GraphItem,
  LabelExportSupport,
  type YGraphState,
} from '@ardoq/graph';
import {
  type LabelStyleProperties,
  createSingleLabelExporter,
} from '@ardoq/export';
import { ensureHeaderLayout } from '../ensureHeaderLayout';
import { GROUP_SUB_LABEL_KIND } from './groupHeaderLabelKind';
import { createLabelStyle, Preset } from '../../shared/styleUtils';
import { getGraphTag, getNodeFromLabel } from '../../shared/layoutUtils';
import { GroupSubLabelVisual } from './GroupSubLabelVisual';

const render = (label: ILabel, groupHeaders: YGraphState['groupHeaders']) => {
  const node = getNodeFromLabel(label);
  if (!node) {
    return null;
  }
  const headerLayout = ensureHeaderLayout(node, groupHeaders);
  if (!headerLayout) {
    return null;
  }
  const { wrappedSubLabel } = headerLayout;
  const {
    layout: {
      bounds: { x, y },
    },
  } = label;
  const { tag } = node;
  const { isTransparentized } = tag as GraphItem;
  return new GroupSubLabelVisual(x, y, wrappedSubLabel, isTransparentized);
};

class GroupSubLabelStyle extends LabelStyleBase {
  static Instance = new GroupSubLabelStyle();
  readonly groupHeaderLabelKind = GROUP_SUB_LABEL_KIND;
  public static getStyle(
    context: IRenderContext,
    label: ILabel
  ): LabelStyleProperties | null {
    const graphTag = getGraphTag(context);
    const { groupHeaders } = graphTag ?? {};
    if (!groupHeaders) return null;

    const node = getNodeFromLabel(label);
    if (!node) return null;

    const groupHeader = ensureHeaderLayout(node, groupHeaders);
    if (!groupHeader) return null;

    const { isTransparentized } = (node.tag as GraphItem) ?? {
      isTransparentized: false,
    };

    const lines =
      (label.tag as string[] | null) ??
      (groupHeader.wrappedSubLabel as string[] | undefined) ??
      [];

    return createLabelStyle(
      null,
      { kind: 'lines', lines },
      { preset: Preset.GroupSub, isTransparentized }
    );
  }
  public static getLayout(context: IRenderContext, label: ILabel): Rect {
    const node = getNodeFromLabel(label);
    if (!node) {
      return Rect.EMPTY;
    }
    const { x, y } = node.layout;
    const { width, height } = label.layout;
    const graphTag = getGraphTag(context);
    const { groupHeaders } = graphTag ?? {};

    if (!groupHeaders) {
      return Rect.EMPTY;
    }

    const headerLayout = ensureHeaderLayout(node, groupHeaders);
    if (!headerLayout) {
      return Rect.EMPTY;
    }
    const yPosition = y + Math.max(22, headerLayout.mainLabelHeight);

    return new Rect(x, yPosition, width, height);
  }
  private constructor() {
    super();
  }

  protected override lookup(label: ILabel, type: Constructor): any {
    if (type === LabelExportSupport) {
      const provider: LabelExportSupport = {
        toJSON: createSingleLabelExporter(
          GroupSubLabelStyle.getLayout,
          GroupSubLabelStyle.getStyle
        ),
      };
      return provider;
    }
    return super.lookup(label, type);
  }

  override createVisual(context: IRenderContext, label: ILabel): Visual | null {
    const graphTag = getGraphTag(context);
    const { groupHeaders } = graphTag ?? {};
    return groupHeaders ? render(label, groupHeaders) : null;
  }
  override getPreferredSize(_label: ILabel): Size {
    return Size.ZERO;
  }
}
export { GroupSubLabelStyle };
